import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import { Link as RouterLink } from 'react-router-dom';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  LocalizationProvider,
  DatePicker,
} from '@mui/x-date-pickers';

import OrderType from './OrderType';
import { useSelector, useDispatch } from 'react-redux';
import { setOrderFromDate, setOrderToDate, setOrderTableNumber } from 'store/actions/restaurant';
import moment from 'moment';

import API from 'util/API';

const useStyles = makeStyles(theme => ({
  root: {},
  row: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      // Wrap only on small screens. The theme forces `width: 100% !important`
      // on every MuiTextField, so wrapping at desktop widths would break the
      // date pickers onto separate lines instead of letting them share a row.
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      // Every control goes full width so nothing is clipped on a 344px foldable.
      '& > *': {
        width: '100%'
      },
      // Stretch the split button as a group; sizing its two halves individually
      // wraps the label.
      '& .MuiButtonGroup-root': {
        width: '100%'
      }
    }
  },
  spacer: {
    flexGrow: 1,
    // A growing spacer inside a wrapping row renders as a blank line.
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  importButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0
    }
  },
  exportButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0
    }
  },
  orderTypeButton: {
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0
    }
  },
  searchInput: {
    marginRight: theme.spacing(1)
  },
  datePicker: {
    width: '160px'
  },
  tableNumberInput: {
    width: '250px'
  },
  searchButton: {
    minWidth: '40px',
    height: '40px',
    padding: theme.spacing(1),
    marginTop: 0
  },
  tableNumberContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  }
}));

const OrdersToolbar = props => {
  const { className, ...rest } = props;
  const classes = useStyles();

  const dispatch = useDispatch();

  const setFromDate = (date) => dispatch(setOrderFromDate(date));
  const setToDate = (date) => dispatch(setOrderToDate(date));
  
  const fromDate = useSelector((state) => state.restaurant.orderFromDate);
  const toDate = useSelector((state) => state.restaurant.orderToDate);
  
  // Use filter text from parent component
  const localFilterText = props.filterText || '';
  
  // Ensure dates are valid Date objects
  const validFromDate = fromDate instanceof Date && !isNaN(fromDate) ? fromDate : new Date();
  const validToDate = toDate instanceof Date && !isNaN(toDate) ? toDate : new Date();
  const orderType = useSelector((state) => state.restaurant.orderType);

  const handleFromDateChange = (date) => {
    console.log('fromDate', date);
    setFromDate(date);
    // If toDate is before the new fromDate, update toDate to match fromDate
    if (toDate < date) {
      setToDate(date);
    }
  };

  const handleToDateChange = (date) => {
    console.log('toDate', date);
    // Validate that toDate is not before fromDate
    if (date >= fromDate) {
      setToDate(date);
    } else {
      // If user tries to set toDate before fromDate, set it to fromDate
      setToDate(fromDate);
    }
  };

  const handleFilterChange = (e) => {
    if (props.onFilterChange) {
      props.onFilterChange(e.target.value);
    }
  };

  const handleSearchClick = () => {
    // Trigger parent search handler if provided
    if (props.onSearch) {
      props.onSearch();
    }
  };


  const downloadCustomers = async () => {
    try {
      const dateStr = moment(fromDate).format('DD-MM-YY');
      const response = await API.get(`/dineCustomers?dateStr=${dateStr}`);
      const data = await response.data;
      // console.log('data', data);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customer-${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();


    } catch (err) {
      console.log(err);
    }
  }



  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <div className={classes.row}>
        <span className={classes.spacer} />
        <Button
          color="primary"
          component={RouterLink}
          startIcon={<SoupKitchenIcon />}
          to="/kitchenStatus"
          variant="outlined"
        >
          Kitchen Status
        </Button>
        {/* 
        <IconButton aria-label="delete"
          onClick={props.deleteProxy}>
          <DeleteIcon />
        </IconButton> */}
        <Button
          className={classes.exportButton}
          color="primary"
          variant="outlined"
          onClick={props.downloadOrders}
        >
          Download Orders CSV
        </Button>
   { orderType === 'online' ?
        <Button
          className={classes.importButton}
          color="primary"
          variant="outlined"
          onClick={downloadCustomers}
        >
          Download Customers
        </Button> : null}

        <div className={classes.orderTypeButton}>
          <OrderType />
        </div>



      </div>
      <div className={classes.row}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="From Date"
            value={validFromDate}
            onChange={handleFromDateChange}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                size: 'small',
                className: classes.datePicker,
                margin: 'none',
                InputProps: {
                  'aria-label': 'change from date',
                }
              }
            }}
          />
          <DatePicker
            label="To Date"
            value={validToDate}
            onChange={handleToDateChange}
            format="dd/MM/yyyy"
            minDate={validFromDate}
            slotProps={{
              textField: {
                size: 'small',
                className: classes.datePicker,
                margin: 'none',
                InputProps: {
                  'aria-label': 'change to date',
                }
              }
            }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearchClick}
        >
          Load Orders
        </Button>
        <span className={classes.spacer} />
      </div>
      <div className={classes.row}>
        <TextField
          label="Filter Orders"
          size="small"
          margin="none"
          className={classes.tableNumberInput}
          value={localFilterText}
          onChange={handleFilterChange}
          placeholder="Table, customer name, or order # (partial OK, e.g. last 4)"
          InputProps={{
            startAdornment: <SearchIcon style={{ marginRight: '8px', color: 'rgba(0, 0, 0, 0.54)' }} />,
          }}
        />
        <span className={classes.spacer} />
      </div>
    </div>
  );
};

OrdersToolbar.propTypes = {
  className: PropTypes.string,
  downloadOrders: PropTypes.func,
  onSearch: PropTypes.func,
  onFilterChange: PropTypes.func,
  filterText: PropTypes.string
};

export default OrdersToolbar;
